import React, { useState } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "../../assets/logo.svg";
import { useAuth } from "../../context/auth.context.tsx";
import {
  bootstrapMfaLogin,
  fetchQrSvgBlobUrl,
  interpretLoginResponse,
  interpretSetupPostResponse,
  postLoginAuthStep,
  postLoginBackupStep,
  postLoginOtpStep,
  MFA_QR_SVG_URL,
} from "../../api/auth/twoFactorClient.ts";
import { useMfaLoginFlow } from "../../features/auth/useMfaLoginFlow.ts";

type Phase = "credentials" | "totp" | "backup" | "setup_qr" | "post_setup";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [phase, setPhase] = useState<Phase>("credentials");
  const [qrSrc, setQrSrc] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const redirectTo = (location.state as { from?: { pathname?: string } })?.from?.pathname;
  const { completeKnoxLogin } = useAuth();
  const { prepareSetupQr, resetSetupState, submitGeneratorToken } = useMfaLoginFlow();

  const resetToCredentials = (message?: string) => {
    resetSetupState();
    setQrSrc((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    setPhase("credentials");
    setOtp("");
    setBackupCode("");
    setSetupToken("");
    if (message) setErrorBanner(message);
  };

  const handleSetupPrepare = async () => {
    setLoading(true);
    setQrSrc((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const state = await prepareSetupQr();
      if (state === "done") {
        setPhase("post_setup");
        return;
      }
      setQrSrc(MFA_QR_SVG_URL);
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : "Could not start authenticator setup.");
    } finally {
      setLoading(false);
    }
  };

  const onQrImageError = () => {
    void (async () => {
      try {
        const url = await fetchQrSvgBlobUrl();
        setQrSrc((prev) => {
          if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
          return url;
        });
      } catch {
        /* keep broken image; user may fix CORS */
      }
    })();
  };

  const handlePasswordSubmit = async () => {
    if (!username.trim() || !password) {
      setErrorBanner("Please enter username and password.");
      return;
    }
    setErrorBanner(null);
    setLoading(true);
    try {
      await bootstrapMfaLogin();
      const res = await postLoginAuthStep(username.trim(), password);
      const outcome = interpretLoginResponse(res);

      switch (outcome.kind) {
        case "knox":
          completeKnoxLogin(outcome.session, redirectTo);
          break;
        case "totp":
          setPhase("totp");
          break;
        case "backup":
          setPhase("backup");
          break;
        case "setup":
          setPhase("setup_qr");
          await handleSetupPrepare();
          break;
        case "auth_failed":
          setErrorBanner("Invalid username or password.");
          break;
        default:
          setErrorBanner("Unexpected response from the server. Try again.");
      }
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async () => {
    const code = otp.replace(/\s/g, "");
    if (code.length !== 6) {
      setErrorBanner("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setErrorBanner(null);
    setLoading(true);
    try {
      const res = await postLoginOtpStep(code);
      const outcome = interpretLoginResponse(res);
      if (outcome.kind === "knox") {
        completeKnoxLogin(outcome.session, redirectTo);
        return;
      }
      setErrorBanner("That code did not work. Try again.");
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackupSubmit = async () => {
    if (!backupCode.trim()) {
      setErrorBanner("Enter a backup code.");
      return;
    }
    setErrorBanner(null);
    setLoading(true);
    try {
      const res = await postLoginBackupStep(backupCode);
      const outcome = interpretLoginResponse(res);
      if (outcome.kind === "knox") {
        completeKnoxLogin(outcome.session, redirectTo);
        return;
      }
      setErrorBanner("That backup code did not work.");
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupVerify = async () => {
    const code = setupToken.replace(/\s/g, "");
    if (code.length !== 6) {
      setErrorBanner("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setErrorBanner(null);
    setLoading(true);
    try {
      const res = await submitGeneratorToken(code);
      const outcome = interpretSetupPostResponse(res);
      if (outcome.kind === "knox") {
        completeKnoxLogin(outcome.session, redirectTo);
        return;
      }
      if (outcome.kind === "html" && outcome.note === "setup_complete") {
        setQrSrc((prev) => {
          if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
          return null;
        });
        setSetupToken("");
        setPhase("post_setup");
        return;
      }
      if (outcome.kind === "html" && outcome.note === "generator_error") {
        setErrorBanner("That code is not valid. Please try again.");
        return;
      }
      if (outcome.kind === "html" && outcome.note?.startsWith("wizard_step:")) {
        setErrorBanner("Your session expired. Please start setup again.");
        return;
      }
      setErrorBanner("We could not complete setup. Please try again.");
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    if (phase === "credentials") void handlePasswordSubmit();
    else if (phase === "totp") void handleTotpSubmit();
    else if (phase === "backup") void handleBackupSubmit();
    else if (phase === "setup_qr") void handleSetupVerify();
  };

  const fieldSx = {
    backgroundColor: "#00000040",
    borderRadius: "5px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#FFFFFF" },
      "&.Mui-focused fieldset": { borderColor: "#FFFFFF" },
      "& input": { color: "#FFFFFF" },
    },
    "& label": { color: "#FFFFFF" },
    "& label.Mui-focused": { color: "#FFFFFF" },
  };

  const title =
    phase === "post_setup"
      ? "Two-factor authentication is enabled"
      : phase === "setup_qr"
        ? "Set up your authenticator"
        : phase === "totp" || phase === "backup"
          ? "Two-factor verification"
          : "Welcome to HQF 2";

  const subtitle =
    phase === "post_setup"
      ? "You're all set! Sign in again with your username, password, and the code from your authenticator app."
      : phase === "setup_qr"
        ? "Scan the QR code with your authenticator app, then enter the 6-digit code."
        : phase === "totp"
          ? "Enter the code from your authenticator app."
          : phase === "backup"
            ? "Enter one of your backup codes."
            : "Sign in with your username and password. If this is your first time, we will walk you through setting up your authenticator app.";

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      bgcolor="#292929"
      onKeyDown={handleKeyDown}
    >
      <Logo style={{ marginBottom: 20, width: "250px", height: "250px" }} />
      <Typography
        variant="h5"
        width={"500px"}
        textAlign={"center"}
        style={{ color: "white", marginBottom: 12, fontWeight: "bold" }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        width={"520px"}
        textAlign={"center"}
        sx={{ color: "#c0c0c0", mb: 2, px: 1 }}
      >
        {subtitle}
      </Typography>

      {errorBanner && (
        <Typography variant="body2" color="error" sx={{ mb: 2, maxWidth: 480, textAlign: "center" }}>
          {errorBanner}
        </Typography>
      )}

      {phase === "post_setup" && (
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#2E7C67",
            color: "#FFFFFF",
            marginTop: "10px",
            "&:hover": { backgroundColor: "#4EBA74", color: "#FFFFFF" },
          }}
          onClick={() => {
            setErrorBanner(null);
            setPhase("credentials");
          }}
        >
          Continue to sign in
        </Button>
      )}

      {phase === "credentials" && (
        <>
          <TextField
            label="Username"
            title="Username"
            variant="outlined"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            focused
            disabled={loading}
            sx={fieldSx}
          />
          <TextField
            label="Password"
            title="Password"
            type="password"
            variant="outlined"
            autoComplete="current-password"
            value={password}
            margin="normal"
            disabled={loading}
            onChange={(e) => setPassword(e.target.value)}
            sx={fieldSx}
          />
          <Button
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#2E7C67",
              color: "#FFFFFF",
              marginTop: "10px",
              "&:hover": { backgroundColor: "#4EBA74", color: "#FFFFFF" },
              "&:active": { backgroundColor: "#000000" },
            }}
            onClick={() => void handlePasswordSubmit()}
          >
            {loading ? "Signing in…" : "Log in"}
          </Button>
        </>
      )}

      {phase === "totp" && (
        <>
          <TextField
            label="Authenticator code"
            variant="outlined"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            margin="normal"
            focused
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
            sx={fieldSx}
          />
          <Button
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#2E7C67",
              color: "#FFFFFF",
              marginTop: "10px",
              "&:hover": { backgroundColor: "#4EBA74" },
            }}
            onClick={() => void handleTotpSubmit()}
          >
            {loading ? "Verifying…" : "Verify and sign in"}
          </Button>
          <Button sx={{ mt: 2, color: "#9e9e9e" }} onClick={() => resetToCredentials()} disabled={loading}>
            Start over
          </Button>
        </>
      )}

      {phase === "backup" && (
        <>
          <TextField
            label="Backup code"
            variant="outlined"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            margin="normal"
            focused
            sx={fieldSx}
          />
          <Button
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#2E7C67",
              color: "#FFFFFF",
              marginTop: "10px",
              "&:hover": { backgroundColor: "#4EBA74" },
            }}
            onClick={() => void handleBackupSubmit()}
          >
            {loading ? "Verifying…" : "Verify with backup code"}
          </Button>
          <Button sx={{ mt: 2, color: "#9e9e9e" }} onClick={() => resetToCredentials()} disabled={loading}>
            Start over
          </Button>
        </>
      )}

      {phase === "setup_qr" && (
        <>
          {qrSrc && (
            <Box sx={{ my: 2, bgcolor: "#fff", p: 1, borderRadius: 1 }}>
              <img
                src={qrSrc}
                alt="QR code for your authenticator app"
                crossOrigin="use-credentials"
                style={{ width: 220, height: 220, display: "block" }}
                onError={onQrImageError}
              />
            </Box>
          )}
          <TextField
            label="Verification code"
            placeholder="000000"
            variant="outlined"
            value={setupToken}
            onChange={(e) => setSetupToken(e.target.value.replace(/\D/g, "").slice(0, 6))}
            margin="normal"
            focused
            inputProps={{ maxLength: 6, inputMode: "numeric", autoComplete: "one-time-code" }}
            sx={fieldSx}
          />
          <Button
            variant="contained"
            disabled={loading || setupToken.length !== 6}
            sx={{
              backgroundColor: "#2E7C67",
              color: "#FFFFFF",
              marginTop: "10px",
              "&:hover": { backgroundColor: "#4EBA74" },
            }}
            onClick={() => void handleSetupVerify()}
          >
            {loading ? "Saving…" : "Confirm and enable"}
          </Button>
          <Button sx={{ mt: 2, color: "#9e9e9e" }} onClick={() => resetToCredentials()} disabled={loading}>
            Cancel setup
          </Button>
        </>
      )}

    </Box>
  );
};

export default Login;
